/**
 * Randomizes numeric values in an array of objects while preserving non-numeric fields.
 * @param data - Array of objects to randomize
 * @param ignoreFields - Object keys to skip during randomization
 * @param variation - Percentage variation for randomization (default: 75%)
 * @returns Array of objects with randomized numeric values
 */
export const randomizeData = <T extends Record<string, any>>({
    data: rawData,
    ignoreFields = [],
    variation,
    toFixed = 0,
}: {
    data: T[]
    ignoreFields?: string[]
    variation?: number
    toFixed?: number
}): T[] => {
    const randomizedData = rawData.map((point) => {
        const randomizedPoint = { ...point }
        Object.keys(point).forEach((key) => {
            if (!ignoreFields.includes(key)) {
                const value = Number(point[key])
                const isNumber = !isNaN(value)

                if (isNumber) {
                    Object.assign(randomizedPoint, {
                        [key]:
                            toFixed === 0
                                ? randomizeValue(value, variation)
                                : randomizeValueWithFixedDecimals(
                                      value,
                                      variation,
                                      toFixed
                                  ),
                    })
                } else {
                    console.log(`Key ${key} is not a number`)
                }
            }
        })
        return randomizedPoint
    })

    return randomizedData
}

/**
 * Applies random variation to a numeric value.
 * @param value - The number to randomize
 * @param variation - Percentage variation (default: 75%)
 * @returns Randomized number rounded to nearest integer
 */
export const randomizeValue = (value: number, variation: number = 75) => {
    const randomVariation = Math.random() * (variation * 2) - variation
    return Math.round(value + (value * randomVariation) / 100)
}

/**
 * Applies random variation to a numeric value.
 * @param value - The number to randomize
 * @param variation - Percentage variation (default: 75%)
 * @returns Randomized number to fixed value
 */
export const randomizeValueWithFixedDecimals = (
    value: number,
    variation: number = 75,
    toFixed = 1
) => {
    const randomVariation = Math.random() * (variation * 2) - variation
    return parseFloat(
        (value + (value * randomVariation) / 100).toFixed(toFixed)
    )
}

/**
 * Randomizes string values that may contain currency symbols and commas.
 * Preserves original formatting (dollar signs and comma separators).
 * @param stringValue - String that may contain numeric value with $ and/or commas
 * @returns Randomized string with original formatting preserved
 */
export const randomizeStringValue = (stringValue: string): string => {
    let value = stringValue
    const hasComma = value.includes(',')
    const hasCurrency = value.includes('$')

    if (hasCurrency) {
        value = value.replace('$', '')
    }

    if (hasComma) {
        value = value.replace(',', '')
    }

    const numericValue = Number(value)
    const isNumber = !isNaN(numericValue)

    if (isNumber) {
        const randomizedNumericValue = randomizeValue(numericValue)
        value = randomizedNumericValue.toLocaleString('en-US')
        if (hasCurrency) value = `$${value}`
    } else {
        return stringValue
    }

    return value
}
